import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Button,
	FormControl,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	Paper,
	Tab,
	Tabs,
	Typography,
	Zoom,
} from "@material-ui/core";
import RebaseTimer from "../../components/RebaseTimer/RebaseTimer";
import TabPanel from "../../components/TabPanel";
import { trim } from "../../helpers";
import { useWeb3Context } from "src/hooks/web3Context";
import { isPendingTxn, txnButtonText } from "src/slices/PendingTxnsSlice";
import { Skeleton } from "@material-ui/lab";
import { error } from "../../slices/MessagesSlice";
import { ethers } from "ethers";
import { abi as GenerationalWealthSocietyStakingv2 } from "../../abi/GenerationalWealthSocietyStakingv2.json";
import BigNumber from 'bignumber.js';
import "./stake.scss";
import { changeApproval, changeStake } from "src/slices/StakeThunk";
import { addresses } from "src/constants";

function a11yProps(index) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

function Stake() {
	const dispatch = useDispatch();
	const { provider, address, connect, chainID } = useWeb3Context();

	const [zoomed, setZoomed] = useState(false);
	const [lockUpTimer, setLockUpTimer] = useState('0');
	const [view, setView] = useState(0);
	const [quantity, setQuantity] = useState("");

	const isAppLoading = useSelector(state => state.app.loading);
	const currentIndex = useSelector(state => {
		return state.app.currentIndex;
	});
	const fiveDayRate = useSelector(state => {
		return state.app.fiveDayRate;
	});
	const gwsBalance = useSelector(state => {
		return state.account.balances && state.account.balances.gws;
	});
	const sgwsBalance = useSelector(state => {
		return state.account.balances && state.account.balances.sgws;
	});
	const stakeAllowance = useSelector(state => {
		return state.account.staking && state.account.staking.gwsStake;
	});
	const unstakeAllowance = useSelector(state => {
		return state.account.staking && state.account.staking.gwsUnstake;
	});
	const stakingRebase = useSelector(state => {
		return state.app.stakingRebase;
	});
	const stakingAPY = useSelector(state => {
		return state.app.stakingAPY;
	});
	const pendingTransactions = useSelector(state => {
		return state.pendingTransactions;
	});
	const currentBlock = useSelector(state => {
		return state.app.currentBlock;
	});

	const setMax = () => {
		if (view === 0) {
			setQuantity(gwsBalance);
		} else {
			setQuantity(sgwsBalance);
		}
	};

	const onSeekApproval = async token => {
		await dispatch(changeApproval({ address, token, provider, networkID: chainID }));
	};

	const onChangeStake = async action => {
		// eslint-disable-next-line no-restricted-globals
		if (isNaN(quantity) || quantity === 0 || quantity === "") {
			// eslint-disable-next-line no-alert
			return dispatch(error("Please enter a value!"));
		}

		// 1st catch if quantity > balance
		let gweiValue = ethers.utils.parseUnits(quantity, "gwei");
		if (action === "stake" && gweiValue.gt(ethers.utils.parseUnits(gwsBalance, "gwei"))) {
			return dispatch(error("You cannot stake more than your GWS balance."));
		}

		if (action === "unstake" && gweiValue.gt(ethers.utils.parseUnits(sgwsBalance, "gwei"))) {
			return dispatch(error("You cannot unstake more than your sGWS balance."));
		}

		dispatch(changeStake({ address, action, value: quantity.toString(), provider, networkID: chainID }));
	};

	const hasAllowance = useCallback(
		token => {
			if (token === "gws") return stakeAllowance > 0;
			if (token === "sgws") return unstakeAllowance > 0;
			return 0;
		},
		[stakeAllowance, unstakeAllowance],
	);

	const isAllowanceDataLoading = (stakeAllowance == null && view === 0) || (unstakeAllowance == null && view === 1);

	let modalButton = [];

	modalButton.push(
		<Button variant="contained" color="primary" className="connect-button" onClick={connect} key={1}>
			Connect Wallet
		</Button>,
	);

	const changeView = (event, newView) => {
		setView(newView);
	};

	const trimmedBalance = Number(
		[sgwsBalance]
			.filter(Boolean)
			.map(balance => Number(balance))
			.reduce((a, b) => a + b, 0)
			.toFixed(4),
	);

	const showValue = (value, valueCheck, additionalString) => {
		if (new BigNumber(value).gt(10000000) || new BigNumber(value).isEqualTo(0) || (valueCheck !== null && new BigNumber(valueCheck).gt(10000000))) {
			return '- %';
		}
		if (!value || isNaN(value)) {
			return <Skeleton width="150px" />;
		}
		if (additionalString) {
			return value + additionalString;
		}

		return value;
	}

	const trimmedStakingAPY = trim(stakingAPY * 100, 1);
	const stakingRebasePercentage = trim(stakingRebase * 100, 4);
	const nextRewardValue = trim((stakingRebasePercentage / 100) * trimmedBalance, 4);

	const rebasesLockup = async () => {
		const stakingContract = new ethers.Contract(
			addresses[chainID].STAKING_ADDRESS,
			GenerationalWealthSocietyStakingv2,
			provider,
		);
		if (address) {
			const epoch = await stakingContract.epoch();
			const initialStake = await stakingContract.initialStake(address);
			if (epoch && epoch.number) {
				const currentEpoch = epoch.number.toString();
				const totalRebases = initialStake.toString();
				if (new BigNumber(totalRebases).isEqualTo(0)) {
					return setLockUpTimer('0')
				}
				return setLockUpTimer(new BigNumber(totalRebases).minus(currentEpoch).toString());
			}
		}
	}

	// This initializes secondsToRebase as soon as currentBlock becomes available
	useMemo(() => {
		if (currentBlock) {
			rebasesLockup();
		}
	}, [currentBlock]);

	return (
		<div className="content-container">
			<div id="stake-view">
				<Zoom in={true} onEntered={() => setZoomed(true)}>
					<div className="stake-wrapper">
						<div className="content-title">
							<Typography variant="h1">Single Stake</Typography>
						</div>
						<div className="stake-hero icon-card-wrapper">
							<div className="icon-card-wrap">
								<Paper className="content-card icon-card">
									<div className="icon-icon icon-blue">
										<i className="fa-light fa-chart-column"></i>
									</div>
									<div className="icon-card-content">
										<Typography variant="h6" color="textSecondary">
											APY
										</Typography>
										<Typography variant="h4">
											{showValue(trimmedStakingAPY, stakingAPY, ' %')}
										</Typography>
									</div>
								</Paper>
							</div>

							<div className="icon-card-wrap">
								<Paper className="content-card icon-card">
									<div className="icon-icon icon-blue">
										<i className="fa-light fa-tag"></i>
									</div>
									<div className="icon-card-content">
										<Typography variant="h6" color="textSecondary">
											Current Index
										</Typography>
										<Typography variant="h4">
											{showValue(trim(currentIndex, 2), null, ' GWS')}
										</Typography>
									</div>
								</Paper>
							</div>
						</div>

						<div className="staking-area">
							{!address ? (
								<Paper className="stake-wallet-notification">
									<div className="wallet-menu" id="wallet-menu">
										{modalButton}
									</div>
									<Typography variant="h6">Connect your wallet to stake </Typography>
								</Paper>
							) : (
								<>
									<Paper className="stake-content">
										<Box className="stake-action-area">
											<RebaseTimer />
											<Tabs
												key={String(zoomed)}
												value={view}
												textColor="primary"
												indicatorColor="primary"
												className="stake-tab-buttons"
												onChange={changeView}
												aria-label="stake tabs"
											>
												<Tab label="Stake" {...a11yProps(0)} />
												<Tab label="Unstake" {...a11yProps(1)} />
											</Tabs>

											<Box className="stake-action-row" display="flex" alignItems="center">
												{address && !isAllowanceDataLoading ? (
													(!hasAllowance("gws") && view === 0) || (!hasAllowance("sgws") && view === 1) ? (
														<Box className="help-text">
															<Typography variant="body1" className="stake-note"
																color="textSecondary">
																{view === 0 ? (
																	<>
																		First time staking <b>GWS</b>?
																		<br />
																		Please approve GWS to use
																		your <b>GWS</b> for staking.
																	</>
																) : (
																	<>
																		First time unstaking <b>sGWS</b>?
																		<br />
																		Please approve sGWS to use
																		your <b>sGWS</b> for unstaking.
																	</>
																)}
															</Typography>
														</Box>
													) : (
														<FormControl className="gws-input" variant="outlined"
															color="primary">
															<InputLabel htmlFor="amount-input"></InputLabel>
															<OutlinedInput
																id="amount-input"
																type="number"
																placeholder="Enter an amount"
																className="stake-input"
																value={quantity}
																onChange={e => setQuantity(e.target.value)}
																labelWidth={0}
																endAdornment={
																	<InputAdornment position="end">
																		<Button variant="text" onClick={setMax}
																			color="inherit">
																			Max
																		</Button>
																	</InputAdornment>
																}
															/>
														</FormControl>
													)
												) : (
													<Skeleton width="150px" />
												)}

												<TabPanel value={view} index={0} className="stake-tab-panel">
													{isAllowanceDataLoading ? (
														<Skeleton />
													) : address && hasAllowance("gws") ? (
														<Button
															className="stake-button"
															variant="contained"
															color="primary"
															disabled={isPendingTxn(pendingTransactions, "staking")}
															onClick={() => {
																onChangeStake("stake");
															}}
														>
															{txnButtonText(pendingTransactions, "staking", "Stake GWS")}
														</Button>
													) : (
														<Button
															className="stake-button"
															variant="contained"
															color="primary"
															disabled={isPendingTxn(pendingTransactions, "approve_staking")}
															onClick={() => {
																onSeekApproval("gws");
															}}
														>
															{txnButtonText(pendingTransactions, "approve_staking", "Approve")}
														</Button>
													)}
												</TabPanel>
												<TabPanel value={view} index={1} className="stake-tab-panel">
													{isAllowanceDataLoading ? (
														<Skeleton />
													) : address && hasAllowance("sgws") ? (
														<Button
															className="stake-button"
															variant="contained"
															color="primary"
															disabled={isPendingTxn(pendingTransactions, "unstaking")}
															onClick={() => {
																onChangeStake("unstake");
															}}
														>
															{txnButtonText(pendingTransactions, "unstaking", "Unstake GWS")}
														</Button>
													) : (
														<Button
															className="stake-button"
															variant="contained"
															color="primary"
															disabled={isPendingTxn(pendingTransactions, "approve_unstaking")}
															onClick={() => {
																onSeekApproval("sgws");
															}}
														>
															{txnButtonText(pendingTransactions, "approve_unstaking", "Approve")}
														</Button>
													)}
												</TabPanel>
											</Box>
										</Box>
									</Paper>
									<Paper className="stake-content">
										<div className="content-title">
											<Typography variant="h2">Overview</Typography>
										</div>
										<div className={`stake-user-data`}>
											<div className="data-row">
												<div className="data-row-col">
													<Typography variant="body1">Your Balance</Typography>
												</div>
												<div className="data-row-col">
													<Typography variant="body1">
														{isAppLoading ?
															<Skeleton width="80px" /> : <>{trim(gwsBalance, 4)} GWS</>}
													</Typography>
												</div>
											</div>


											<div className={`data-row ${new BigNumber(lockUpTimer).gt(0) ? '' : 'data-row-end'}`}>
												<div className="data-row-col">
													<Typography variant="body1">Your Staked Balance</Typography>
												</div>
												<div className="data-row-col">
													<Typography variant="body1">
														{isAppLoading ? <Skeleton width="80px" /> : <>{trimmedBalance} sGWS</>}
													</Typography>
												</div>
											</div>

															{console.log('lockUpTimer', lockUpTimer)}
											{new BigNumber(lockUpTimer).gt(0) &&
												<div className="data-row data-row-end">
													<div className="data-row-col">
														<Typography variant="body1">Lockup Rebases Left</Typography>
													</div>
													<div className="data-row-col">
														<Typography variant="body1">
															{isAppLoading ? <Skeleton width="80px" /> : lockUpTimer}
														</Typography>
													</div>
												</div>
											}
											<div className="data-row">
												<div className="data-row-col">
													<Typography variant="body1">Next Reward Amount</Typography>
												</div>
												<div className="data-row-col">
													<Typography variant="body1">
														{isAppLoading ? <Skeleton width="80px" /> : <>{new BigNumber(nextRewardValue).isEqualTo(0) ? '-' : nextRewardValue + ' sGWS'}</>}
													</Typography>
												</div>
											</div>

											<div className="data-row">
												<div className="data-row-col">
													<Typography variant="body1">Next Reward Yield</Typography>
												</div>
												<div className="data-row-col">
													<Typography variant="body1">
														{isAppLoading ? <Skeleton width="80px" /> : <>{new BigNumber(stakingRebasePercentage).isEqualTo(0) ? '-' : stakingRebasePercentage + ' %'}</>}
													</Typography>
												</div>
											</div>

											<div className="data-row">
												<div className="data-row-col">
													<Typography variant="body1">ROI (5-Day Rate)</Typography>
												</div>
												<div className="data-row-col">
													<Typography variant="body1">
														{isAppLoading ? <Skeleton width="80px" /> : <>{new BigNumber(fiveDayRate).isEqualTo(0) ? '-' : trim(fiveDayRate * 100, 4) + ' %'}</>}
													</Typography>
												</div>
											</div>
										</div>
									</Paper>
								</>
							)}
						</div>

					</div>
				</Zoom>

				{/* <ExternalStakePool /> */}
			</div>
		</div>
	);
}

export default Stake;
